import React, { useRef, useState } from "react";

import Button from "@mui/material/Button";
import Modal from "@mui/material/Modal";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import MuiTextField from "@mui/material/TextField";
import Warning from "@mui/icons-material/Warning";

import {
  originalAddressReadable,
  suggestedAddressReadable,
  convertSuggestedAddressToFormFormat,
  convertSuggestedAddressToStringStateFormat,
} from "./addressValidationHelpers";

const modalContainerStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  backgroundColor: "#fff",
  padding: "24px",
  borderRadius: "8px",
  maxWidth: "500px",
  width: "90%",
  maxHeight: "90vh",
  overflow: "auto",
};

const addressSectionStyle = {
  border: "1px solid #ddd",
  borderRadius: "4px",
  padding: "12px",
  marginBottom: "12px",
  cursor: "pointer",
};

const selectedAddressSectionStyle = {
  ...addressSectionStyle,
  border: "2px solid #3f51b5",
  backgroundColor: "#f5f5ff",
};

const buttonContainerStyle = {
  display: "flex",
  justifyContent: "flex-end",
  gap: "12px",
  marginTop: "20px",
};

const buttonStyle = {
  padding: "8px 24px",
};

const backButtonStyle = {
  ...buttonStyle,
  backgroundColor: "#f5f5f5",
};

const sectionHeaderStyle = {
  fontSize: "12px",
  fontWeight: "600",
  color: "#666",
  marginBottom: "4px",
  textTransform: "uppercase",
};

const warningStyle = {
  display: "flex",
  alignItems: "flex-start",
  gap: "12px",
  padding: "12px",
  backgroundColor: "#fff3cd",
  borderRadius: "4px",
  marginBottom: "12px",
};

export function ConfirmAddressModal({
  showModal,
  setShowModal,
  modalData = { originalAddress: {} },
  submit,
  useStringStateFormat = false,
}) {
  const [selectedIdx, setSelectedIdx] = useState("1");

  const handleConfirm = () => {
    if (selectedIdx === "0") {
      submit();
    } else {
      const convertFn = useStringStateFormat
        ? convertSuggestedAddressToStringStateFormat
        : convertSuggestedAddressToFormFormat;
      submit(convertFn(modalData.suggestedAddress));
    }
  };

  return (
    <Modal open={showModal} onClose={() => setShowModal({})}>
      <div style={modalContainerStyle}>
        <h3 style={{ marginTop: 0 }}>Confirm your address</h3>
        <p style={{ marginBottom: "20px", color: "#666" }}>
          Review the recommended changes
        </p>

        <RadioGroup
          value={selectedIdx}
          onChange={(e) => setSelectedIdx(e.target.value)}
        >
          <div
            onClick={() => setSelectedIdx("0")}
            style={
              selectedIdx === "0"
                ? selectedAddressSectionStyle
                : addressSectionStyle
            }
          >
            <div style={{ display: "flex", alignItems: "flex-start" }}>
              <Radio value="0" style={{ padding: "0 8px 0 0" }} />
              <div>
                <h5 style={sectionHeaderStyle}>What you entered</h5>
                <p style={{ whiteSpace: "pre-line", margin: 0 }}>
                  {originalAddressReadable(modalData.originalAddress)}
                </p>
              </div>
            </div>
          </div>

          <div
            onClick={() => setSelectedIdx("1")}
            style={
              selectedIdx === "1"
                ? selectedAddressSectionStyle
                : addressSectionStyle
            }
          >
            <div style={{ display: "flex", alignItems: "flex-start" }}>
              <Radio value="1" style={{ padding: "0 8px 0 0" }} />
              <div>
                <h5 style={sectionHeaderStyle}>Recommended</h5>
                <p style={{ whiteSpace: "pre-line", margin: 0 }}>
                  {modalData &&
                    modalData.suggestedAddress &&
                    suggestedAddressReadable(modalData.suggestedAddress)}
                </p>
              </div>
            </div>
          </div>
        </RadioGroup>

        <div style={buttonContainerStyle}>
          <Button onClick={() => setShowModal({})} style={backButtonStyle}>
            Back
          </Button>
          <Button
            onClick={handleConfirm}
            color="primary"
            variant="contained"
            style={buttonStyle}
          >
            Confirm
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export function MissingAddressComponentsModal({
  showModal,
  setShowModal,
  modalData = { originalAddress: {} },
  submit,
}) {
  const address2Ref = useRef(null);

  return (
    <Modal open={showModal} onClose={() => setShowModal({})}>
      <div style={modalContainerStyle}>
        <h3 style={{ marginTop: 0 }}>Missing Information</h3>
        <p style={{ marginBottom: "20px", color: "#666" }}>
          Does your building have multiple units?
        </p>

        <h5 style={sectionHeaderStyle}>What you entered</h5>
        <div style={addressSectionStyle}>
          <p style={{ whiteSpace: "pre-line", margin: 0 }}>
            {originalAddressReadable(modalData.originalAddress)}
          </p>
        </div>

        <h5 style={sectionHeaderStyle}>Missing information</h5>

        <MuiTextField
          id="address2"
          variant="outlined"
          label="Apt, suite, or unit"
          fullWidth
          inputRef={address2Ref}
          autoFocus
          autoComplete="off"
        />

        <p style={{ fontSize: "12px", color: "#666", marginTop: "8px" }}>
          Leave blank if there are not multiple units
        </p>

        <div style={buttonContainerStyle}>
          <Button onClick={() => setShowModal({})} style={backButtonStyle}>
            Go back
          </Button>
          <Button
            onClick={() => {
              if (address2Ref.current?.value) {
                submit({
                  address2: address2Ref.current.value,
                });
              } else {
                submit();
              }
            }}
            color="primary"
            variant="contained"
            style={buttonStyle}
          >
            Confirm
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export function CouldntVerifyAddressModal({
  showModal,
  setShowModal,
  modalData = {},
  submit,
}) {
  const unconfirmedComponents = modalData.unconfirmedComponents || {};
  const originalAddress = modalData.originalAddress || {};
  const stateName =
    typeof originalAddress.usState === "string"
      ? originalAddress.usState
      : originalAddress.usState?.name;

  return (
    <Modal open={showModal} onClose={() => setShowModal({})}>
      <div style={modalContainerStyle}>
        <h3 style={{ marginTop: 0 }}>We couldn't verify your address</h3>
        <p style={{ marginBottom: "20px", color: "#666" }}>
          Review the verification issues
        </p>

        <h5 style={sectionHeaderStyle}>What you entered</h5>
        <div style={addressSectionStyle}>
          <p style={{ whiteSpace: "pre-line", margin: 0 }}>
            {originalAddressReadable(modalData.originalAddress)}
          </p>
        </div>

        <h5 style={sectionHeaderStyle}>Verification issues</h5>

        {unconfirmedComponents.administrative_area_level_1 ? (
          <div style={warningStyle}>
            <Warning style={{ color: "#856404" }} />
            <div>
              <strong>Can't find this address in</strong>
              <p style={{ margin: "4px 0 0 0" }}>{stateName}</p>
            </div>
          </div>
        ) : unconfirmedComponents.route ? (
          <div style={warningStyle}>
            <Warning style={{ color: "#856404" }} />
            <div>
              <strong>Address not found</strong>
              <p style={{ margin: "4px 0 0 0" }}>
                Please make sure you've entered address correctly
              </p>
            </div>
          </div>
        ) : unconfirmedComponents.street_number ? (
          <div style={warningStyle}>
            <Warning style={{ color: "#856404" }} />
            <div>
              <strong>Address number not found</strong>
              <p style={{ margin: "4px 0 0 0" }}>
                {unconfirmedComponents.street_number}
              </p>
            </div>
          </div>
        ) : unconfirmedComponents.subpremise ? (
          <div style={warningStyle}>
            <Warning style={{ color: "#856404" }} />
            <div>
              <strong>Building unit not found</strong>
              <p style={{ margin: "4px 0 0 0" }}>
                {unconfirmedComponents.subpremise}
              </p>
            </div>
          </div>
        ) : null}

        {unconfirmedComponents.locality && (
          <div style={warningStyle}>
            <Warning style={{ color: "#856404" }} />
            <div>
              <strong>Can't confirm this city in {stateName}</strong>
              <p style={{ margin: "4px 0 0 0" }}>
                {unconfirmedComponents.locality}
              </p>
            </div>
          </div>
        )}

        <div style={buttonContainerStyle}>
          <Button onClick={() => submit({})} style={backButtonStyle}>
            Use entered
          </Button>
          <Button
            onClick={() => setShowModal({})}
            color="primary"
            variant="contained"
            style={buttonStyle}
          >
            Make changes
          </Button>
        </div>
      </div>
    </Modal>
  );
}
